import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, ArrowRight, Crown, Building2, Zap, ExternalLink, Receipt, Sparkles, RefreshCw, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [working, setWorking] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      setPollCount(attempts);
      try {
        const s = await api.checkoutStatus(sessionId);
        if (s.payment_status === "paid") {
          toast.success("You're in. Welcome, operator.");
          // strip query
          const u = new URL(window.location.href); u.search = ""; window.history.replaceState({}, "", u.toString());
          await refresh();
          return;
        }
        if (attempts > 8) {
          toast.message("Payment is still processing — refresh in a moment.");
          return;
        }
        if (!cancelled) setTimeout(poll, 2000);
      } catch (e) {
        toast.error("Could not verify payment yet — refresh in a moment.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params]);

  const refresh = async () => {
    try { setMe(await api.billingMe()); } catch (e) { /* ignore */ }
  };

  useEffect(() => { refresh(); }, []);

  const handlePortal = async () => {
    setWorking(true);
    try {
      const { url } = await api.billingPortal(window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal not available");
      setWorking(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Cancel your subscription? You'll keep access until period end.")) return;
    setWorking(true);
    try {
      await api.cancelSub();
      await refresh();
      toast.success("Cancellation scheduled at period end.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally { setWorking(false); }
  };

  if (!me) return <div className="text-sm mono uppercase text-[#A1A1AA]">loading billing…</div>;

  const tier = me.tier;
  const isActive = me.status === "active" || me.status === "trialing";
  const isCreator = me.is_creator;

  return (
    <div className="max-w-5xl space-y-8" data-testid="billing-dashboard">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-eyebrow mb-2">Membership</div>
          <h1 className="font-display font-black tracking-tighter text-5xl">Your plan & the math behind it.</h1>
        </div>
        <button onClick={refresh} className="text-eyebrow flex items-center gap-1 hover:text-black"><RefreshCw size={12}/> Refresh</button>
      </div>

      {/* CURRENT PLAN */}
      <div className={`border-2 ${isActive ? "border-black" : "border-[#E2E4E9]"} bg-white p-8`} data-testid="current-plan-card">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <div className="text-eyebrow mb-2">Current plan</div>
            <div className="flex items-center gap-2 mb-2">
              {tier === "operator" && <Zap size={22} />}
              {tier === "mogul" && <Building2 size={22} />}
              {tier === "founding" && <Crown size={22} className="text-[#F59E0B]" />}
              <h2 className="font-display font-black text-4xl tracking-tighter">
                {isCreator ? "Creator (free Mogul access)" : tier ? tier.toUpperCase() : "NOT SUBSCRIBED"}
              </h2>
            </div>
            <p className="text-sm text-[#52525B] mb-4">
              {isCreator ? "You're the creator — your account bypasses billing entirely." :
                isActive ? `Active · ${me.interval || ""} · ${me.limits?.label}` :
                "No active plan. Pick a tier to unlock the engine."}
            </p>
            {me.cancel_at_period_end && (
              <div className="border border-[#F59E0B] bg-[#FFFBEB] p-3 text-sm mb-3">
                Cancellation scheduled at period end. You'll keep access until then.
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              {!isActive && (
                <button onClick={() => navigate("/pricing")} className="bg-black text-white px-5 py-3 text-sm flex items-center gap-2 hover:bg-[#002FA7]" data-testid="see-plans-button">
                  See plans <ArrowRight size={14}/>
                </button>
              )}
              {isActive && !isCreator && me.interval !== "lifetime" && (
                <>
                  <button onClick={handlePortal} disabled={working} className="border-2 border-black px-5 py-3 text-sm hover:bg-black hover:text-white flex items-center gap-2" data-testid="manage-billing-button">
                    <ExternalLink size={14}/> Manage billing (Stripe portal)
                  </button>
                  {!me.cancel_at_period_end && (
                    <button onClick={handleCancel} disabled={working} className="border border-[#E2E4E9] px-5 py-3 text-sm text-[#52525B] hover:text-[#E11D48] hover:border-[#E11D48]" data-testid="cancel-button">
                      Cancel at period end
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="bg-[#FAFAFA] border border-black p-5 space-y-3">
              <Stat k="Brands managed" v={me.usage?.brands ?? 0} />
              <Stat k="Posts published" v={me.usage?.posts_published ?? 0} />
              <Stat k="Plan limits" v={me.limits?.label || "—"} />
              <Stat k="Status" v={(me.status || "inactive").toUpperCase()} pos={isActive} />
            </div>
          </div>
        </div>
      </div>

      {/* COST TRANSPARENCY */}
      <div className="border border-black p-8" data-testid="cost-transparency">
        <div className="flex items-center gap-2 mb-1">
          <Receipt size={18} />
          <div className="text-eyebrow">Total cost transparency</div>
        </div>
        <h3 className="font-display font-black tracking-tighter text-2xl mb-4">Everything you actually pay to make this run.</h3>
        <p className="text-sm text-[#52525B] mb-5">No hidden fees. No usage gotchas. Here's every dollar.</p>
        <div className="space-y-3 text-sm">
          {me.third_party_costs?.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 border-b border-[#E2E4E9] pb-3">
              <div className="col-span-4 font-semibold">{c.name}</div>
              <div className="col-span-3 mono">{c.cost}</div>
              <div className="col-span-3 text-xs">
                {c.required ? <span className="text-[#E11D48]">Required</span> : <span className="text-[#52525B]">Optional</span>}
              </div>
              <div className="col-span-2 text-right">
                <a href={c.url} target="_blank" rel="noreferrer" className="text-eyebrow hover:text-black inline-flex items-center gap-1">
                  Provider <ExternalLink size={11} />
                </a>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs mono text-[#A1A1AA] mt-4">We charge separately for Vitality. Substack revenue is yours, 100%.</p>
      </div>

      {/* AFFILIATE TEASER */}
      <div className="bg-[#FFFBEB] border-2 border-[#F59E0B] p-8" data-testid="affiliate-teaser">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-eyebrow text-[#B45309] mb-2">Earn while you publish</div>
            <h3 className="font-display font-black tracking-tighter text-3xl">40% recurring on every friend you refer.</h3>
            <p className="text-sm text-[#52525B] mt-1 max-w-xl">Built-in. No application. Climb to Diamond → 50% + $5K bonus.</p>
          </div>
          <button onClick={() => navigate("/affiliate")} className="bg-black text-white px-6 py-3 text-sm flex items-center gap-2 hover:bg-[#B45309]" data-testid="open-affiliate-button">
            Open my affiliate dashboard <ArrowRight size={14}/>
          </button>
        </div>
      </div>

      {/* TRUST BAR */}
      <div className="grid md:grid-cols-3 gap-4">
        <Trust icon={ShieldCheck} t="Cancel anytime" d="From the Stripe portal. No retention calls."/>
        <Trust icon={Sparkles} t="No usage caps for Mogul" d="Unlimited brands. Unlimited AI words."/>
        <Trust icon={CreditCard} t="Secure billing by Stripe" d="We never touch your card data."/>
      </div>
    </div>
  );
}

function Stat({ k, v, pos }) {
  return (
    <div className="flex items-baseline justify-between border-b border-[#E2E4E9] pb-2 last:border-0">
      <span className="text-eyebrow">{k}</span>
      <span className={`font-display font-bold text-lg ${pos ? "text-[#059669]" : ""}`}>{v}</span>
    </div>
  );
}

function Trust({ icon: Icon, t, d }) {
  return (
    <div className="border border-[#E2E4E9] p-4 bg-white">
      <Icon size={18} className="mb-2"/>
      <div className="font-semibold text-sm">{t}</div>
      <div className="text-xs text-[#52525B] mt-1 leading-relaxed">{d}</div>
    </div>
  );
}
